import { ICarImageElement, IFullStackSession } from 'data/schema';
import type { PhotoSource } from 'components/PhotoSourceToggle/PhotoSourceToggle';

/**
 * Photo selection utilities for Stream View
 */

/**
 * Checks if a photos array has items
 * @param photos - Array of photos or undefined
 * @returns true if photos exist and have length > 0
 */
export const hasPhotos = <T>(photos: T[] | undefined): boolean => {
  return !!photos && photos.length > 0;
};

/**
 * Determines if the photo source toggle should be shown
 * @param hasCarImages - Whether rendered car images are available
 * @param hasVtpPhotos - Whether VTP real photos are available
 * @param isDealer - Whether the current user is a dealer
 * @param comparisonMode - Current comparison mode
 * @returns true if toggle should be visible
 */
export const shouldShowPhotoToggle = (
  hasCarImages: boolean,
  hasVtpPhotos: boolean,
  isDealer: boolean,
  comparisonMode: IFullStackSession['comparisonMode']
): boolean => {
  return hasCarImages && hasVtpPhotos && isDealer && comparisonMode !== 'on';
};

/**
 * Gets the preview image URL for the alternate photo source
 * @param currentSource - Currently selected photo source
 * @param vtpImageElements - Converted VTP photos as car image elements
 * @param carImages - Rendered car images
 * @returns URL of the first image from the alternate source
 */
export const getAlternatePreviewImage = (
  currentSource: PhotoSource,
  vtpImageElements: ICarImageElement[] | undefined,
  carImages: ICarImageElement[] | undefined
): string => {
  if (currentSource === 'render') {
    // Currently showing rendered, preview shows first VTP photo
    return vtpImageElements?.[0]?.source ?? '';
  } else {
    // Currently showing VTP, preview shows first rendered photo
    return carImages?.[0]?.source ?? '';
  }
};

/**
 * Selects the appropriate gallery assets based on photo source and availability
 * Logic:
 * - When both available: use toggle state
 * - When only vtpPhotos: show vtpPhotos
 * - When only carImages: show carImages
 * - When neither: return empty array
 *
 * @param photoSource - Currently selected photo source
 * @param hasCarImages - Whether rendered car images are available
 * @param hasVtpPhotos - Whether VTP real photos are available
 * @param vtpImageElements - Converted VTP photos as car image elements
 * @param carImages - Rendered car images
 * @returns Array of car image elements to display in gallery
 */
export const selectGalleryAssets = (
  photoSource: PhotoSource,
  hasCarImages: boolean,
  hasVtpPhotos: boolean,
  vtpImageElements: ICarImageElement[] | undefined,
  carImages: ICarImageElement[] | undefined
): ICarImageElement[] => {
  if (hasCarImages && hasVtpPhotos) {
    // Both available - use toggle state
    return photoSource === 'real' ? (vtpImageElements ?? []) : (carImages ?? []);
  } else if (hasVtpPhotos) {
    // Only VTP photos available
    return vtpImageElements ?? [];
  }
  // Only carImages or neither
  return carImages ?? [];
};
